import React, { useState, useEffect, useCallback } from 'react';
import SignalBadge from '../components/SignalBadge';
import { fetchIndicators, fetchIndices, fetchIndexContributors } from '../api';

const REFRESH_INTERVAL = 15000;

export default function Dashboard() {
  const [data, setData] = useState(null);
  const [indices, setIndices] = useState([]);
  const [contributors, setContributors] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);

  const loadData = useCallback(async () => {
    try {
      const [indRes, idxRes] = await Promise.allSettled([
        fetchIndicators(),
        fetchIndices(),
      ]);
      if (indRes.status === 'fulfilled' && indRes.value.data?.data) setData(indRes.value.data.data);
      if (idxRes.status === 'fulfilled' && idxRes.value.data?.indices) setIndices(idxRes.value.data.indices);
      // Fetch contributors separately (heavier call)
      try {
        const contribRes = await fetchIndexContributors('Nifty');
        if (contribRes.data?.contributors) setContributors(contribRes.data);
      } catch {}
      setLastRefresh(new Date().toLocaleTimeString());
    } catch {}
  }, []);

  useEffect(() => {
    loadData();
    const interval = setInterval(loadData, REFRESH_INTERVAL);
    return () => clearInterval(interval);
  }, [loadData]);

  const niftyChange = data ? data.nifty_price - (data.nifty_prev_close || 0) : 0;
  const niftyChangePct = data && data.nifty_prev_close > 0 ? ((niftyChange / data.nifty_prev_close) * 100) : 0;

  return (
    <div className="page-dashboard">
      {/* Page Header */}
      <div className="dash-page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">Market overview and index analysis</p>
        </div>
        {lastRefresh && (
          <span className="live-indicator">
            <span className="live-pulse" /> Updated {lastRefresh}
          </span>
        )}
      </div>

      {/* Indices Ticker Strip */}
      {indices.length > 0 && (
        <section className="indices-ticker-strip">
          {indices.map((idx) => (
            <div key={idx.token} className="index-ticker-item">
              <span className="index-ticker-name">{idx.name}</span>
              <span className="index-ticker-ltp">{idx.ltp?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              <span className={`index-ticker-change ${idx.change >= 0 ? 'positive' : 'negative'}`}>
                {idx.change >= 0 ? '▲' : '▼'} {Math.abs(idx.change_pct).toFixed(2)}% ({idx.change >= 0 ? '+' : ''}{idx.change?.toFixed(1)})
              </span>
            </div>
          ))}
        </section>
      )}

      {/* Market Overview Cards */}
      {data && (
        <section className="dash-market-row">
          <div className="dash-card dash-card-nifty">
            <div className="dash-card-icon">📊</div>
            <div className="dash-card-body">
              <span className="dash-card-label">NIFTY 50</span>
              <span className="dash-card-value">{data.nifty_price?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              <span className={`dash-card-change ${niftyChange >= 0 ? 'positive' : 'negative'}`}>
                {niftyChange >= 0 ? '▲' : '▼'} {niftyChangePct.toFixed(2)}% ({niftyChange >= 0 ? '+' : ''}{niftyChange.toFixed(0)} pts)
              </span>
            </div>
          </div>
          <div className="dash-card dash-card-vix">
            <div className="dash-card-icon">📈</div>
            <div className="dash-card-body">
              <span className="dash-card-label">India VIX</span>
              <span className="dash-card-value">{data.vix_value?.toFixed(2)}</span>
              <span className={`dash-card-change ${(data.vix_change_pct || 0) >= 0 ? 'positive' : 'negative'}`}>
                {(data.vix_change_pct || 0) >= 0 ? '▲' : '▼'} {Math.abs(data.vix_change_pct || 0).toFixed(2)}%
              </span>
            </div>
          </div>
          <div className="dash-card dash-card-bias">
            <div className="dash-card-icon">🎯</div>
            <div className="dash-card-body">
              <span className="dash-card-label">Market Bias</span>
              <SignalBadge signal={data.overall_bias} />
              <span className="dash-bias-counts">
                <span className="bias-mini-b">▲ {data.bullish_count}</span>
                <span className="bias-mini-r">▼ {data.bearish_count}</span>
              </span>
            </div>
          </div>
        </section>
      )}

      {!data && (
        <div className="dash-loading-strip">
          <div className="spinner" /> Waiting for market data...
        </div>
      )}

      {/* Day Range Strip */}
      {data && (
        <section className="dash-range-strip">
          <div className="range-item">
            <span className="range-label">Day Low</span>
            <span className="range-val">{data.nifty_low?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="range-bar">
            <div className="range-bar-fill" style={{
              left: data.nifty_low && data.nifty_high && data.nifty_high !== data.nifty_low
                ? `${((data.nifty_price - data.nifty_low) / (data.nifty_high - data.nifty_low)) * 100}%`
                : '50%'
            }} />
          </div>
          <div className="range-item">
            <span className="range-label">Day High</span>
            <span className="range-val">{data.nifty_high?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
          <div className="range-divider" />
          <div className="range-item">
            <span className="range-label">Range</span>
            <span className="range-val">{data.intraday_range?.toFixed(0)} pts</span>
          </div>
          <div className="range-item">
            <span className="range-label">Prev Close</span>
            <span className="range-val">{data.nifty_prev_close?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
          </div>
        </section>
      )}

      {/* Index Contributors */}
      {contributors && contributors.contributors?.length > 0 && (
        <section className="dash-section">
          <div className="dash-section-header">
            <h2>Nifty 50 — Stock Contributions</h2>
            <span className="contrib-index-level">
              Index: {contributors.index_level?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}
              {' · '}
              <span className={contributors.total_contribution >= 0 ? 'positive' : 'negative'}>
                Net: {contributors.total_contribution >= 0 ? '+' : ''}{contributors.total_contribution?.toFixed(1)} pts
              </span>
            </span>
          </div>
          <div className="contrib-grid">
            {contributors.contributors.map((c) => (
              <div key={c.symbol} className={`contrib-chip ${c.contribution >= 0 ? 'contrib-up' : 'contrib-down'}`}>
                <div className="contrib-chip-top">
                  <span className="contrib-symbol">{c.name}</span>
                  <span className="contrib-weight">{c.weight}%</span>
                </div>
                <div className="contrib-chip-mid">
                  <span className="contrib-ltp">{c.ltp?.toLocaleString('en-IN', { minimumFractionDigits: 2 })}</span>
                  <span className={`contrib-pct ${c.pct_change >= 0 ? 'positive' : 'negative'}`}>
                    {c.pct_change >= 0 ? '▲' : '▼'} {Math.abs(c.pct_change).toFixed(2)}%
                  </span>
                </div>
                <div className="contrib-chip-bot">
                  <span className={`contrib-pts ${c.contribution >= 0 ? 'positive' : 'negative'}`}>
                    {c.contribution >= 0 ? '+' : ''}{c.contribution.toFixed(1)} pts
                  </span>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}


    </div>
  );
}
